import { Fragment, type ReactNode, useState } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Divider } from "simplydo/react/ui/divider";
import { Label } from "simplydo/react/ui/label";

import util from "utils/utils";

import type {
  ImpactStatementWithCreator,
  MappedImpactCategory,
  OrganisationImpact,
} from "../types";

type StatementDefinition = NonNullable<
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["statementDefinition"]
>;

type ImpactStatementCategoriesProps = {
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  statementDefinition: StatementDefinition;
  impactStatement?: ImpactStatementWithCreator;
  mappedImpactCategories: MappedImpactCategory[];
  hideUnselectedImpacts?: boolean;
  renderImpact: (impact: OrganisationImpact, index: number) => ReactNode;
};

export const ImpactStatementCategories = ({
  stage,
  statementDefinition,
  impactStatement,
  mappedImpactCategories,
  hideUnselectedImpacts = false,
  renderImpact,
}: ImpactStatementCategoriesProps) => {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const toggleExpanded = (groupKey: string) => {
    setExpandedIds((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  return (
    <Accordion styled fluid>
      {mappedImpactCategories.map((group) => {
        const groupKey = group._id ?? group.name;
        const isExpanded = !!expandedIds[groupKey];
        const enabledImpacts = group?.impacts?.filter(
          (impact) =>
            statementDefinition?.impacts?.[impact._id]?.enabled &&
            !statementDefinition?.impacts?.[impact._id]?.hiddenFrom?.includes(
              stage,
            ),
        );
        if (!enabledImpacts || enabledImpacts.length === 0) {
          return null;
        }

        const chosenImpacts = enabledImpacts.filter(
          (impact) => impactStatement?.impacts?.[impact._id]?.enabled,
        );
        const visibleImpacts = hideUnselectedImpacts
          ? chosenImpacts
          : enabledImpacts;

        if (!visibleImpacts || visibleImpacts.length === 0) {
          return null;
        }

        return (
          <Fragment key={groupKey}>
            <AccordionTitle
              active={isExpanded}
              onClick={() => {
                toggleExpanded(groupKey);
              }}
              label={
                <Label
                  size="small"
                  color={chosenImpacts.length > 0 ? "blue" : "grey"}
                >
                  {hideUnselectedImpacts ? (
                    <>
                      {chosenImpacts?.length || 0}{" "}
                      {util.pluralise(
                        chosenImpacts?.length || 0,
                        `${t("generic.impact")}`,
                        `${t("generic.impacts")}`,
                        false,
                      )}{" "}
                      selected
                    </>
                  ) : (
                    <>
                      {chosenImpacts?.length || 0} /{" "}
                      {enabledImpacts?.length || 0}{" "}
                      {util.pluralise(
                        chosenImpacts?.length || 0,
                        `${t("generic.impact")}`,
                        `${t("generic.impacts")}`,
                        false,
                      )}{" "}
                      selected
                    </>
                  )}
                </Label>
              }
            >
              {group.name}
            </AccordionTitle>
            <AccordionContent active={isExpanded}>
              {visibleImpacts.map((impact, index) => (
                <div key={impact._id}>
                  {index !== 0 ? <Divider section /> : null}
                  {renderImpact(impact, index)}
                </div>
              ))}
            </AccordionContent>
          </Fragment>
        );
      })}
    </Accordion>
  );
};
